import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import { clasesDeColor, cuentaAtras, etiquetaDeEstado, etiquetaDeSuministro } from '../lib/estados'
import { puede } from '../lib/permisos'
import type { Catalogos, Ficha, Usuario } from '../lib/tipos'

const fecha = (valor: string | null | undefined): string | null =>
  valor === null || valor === undefined || valor === ''
    ? null
    : new Date(String(valor).replace(' ', 'T')).toLocaleDateString('es-ES')

const momento = (valor: string | null | undefined): string | null =>
  valor === null || valor === undefined || valor === ''
    ? null
    : new Date(String(valor).replace(' ', 'T') + 'Z').toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })

/**
 * El contrato entero en una pantalla.
 *
 * «Que haya un apartado de resumen donde se despliegue toda la información del
 * contrato», del 15 de octubre, con permiso por grupo. Es para los contratos ya
 * lanzados: a quien lo abre después no le interesa ir fase por fase, le interesa
 * verlo todo de una vez.
 *
 * No hay nada editable aquí a propósito: para cambiar algo se va a su fase.
 */
export function ResumenContrato({
  ficha,
  catalogos,
  usuario,
}: {
  ficha: Ficha
  catalogos: Catalogos
  usuario: Usuario
}) {
  const clienteQuery = useQueryClient()
  const [aviso, setAviso] = useState<string | null>(null)
  const datos = ficha.datos as Record<string, unknown>
  const texto = (clave: string): string | null => {
    const valor = datos[clave]
    return valor === null || valor === undefined || valor === '' ? null : String(valor)
  }

  const compania = catalogos.companias.find((una) => una.id === ficha.compania_id)
  const tarifa = catalogos.tarifas.find((una) => una.codigo === ficha.tarifa_codigo)
  const provincia = catalogos.provincias.find((una) => una.codigo === texto('provincia'))
  const plazo = cuentaAtras(ficha.vence_el)
  const puedeAmpliar = puede(usuario, 'contratos.ampliar_revision')

  const ampliar = useMutation({
    mutationFn: (horas: number) =>
      api(`/api/contratos/${ficha.uuid}/plazo`, { metodo: 'POST', cuerpo: { horas } }),
    onSuccess: () => {
      setAviso(null)
      void clienteQuery.invalidateQueries({ queryKey: ['ficha', ficha.uuid] })
      void clienteQuery.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido alargar el plazo'),
  })

  const direccion = [
    [texto('via'), texto('direccion'), texto('numero')].filter(Boolean).join(' '),
    texto('piso'),
    [texto('cp'), texto('localidad')].filter(Boolean).join(' '),
    provincia?.nombre ?? null,
  ]
    .filter((trozo) => trozo !== null && trozo !== '')
    .join(', ')

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-bold">Resumen del contrato</h2>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${clasesDeColor(ficha.estado_color)}`}>
          {etiquetaDeEstado(ficha)}
        </span>
        {plazo && (
          <span
            title="Horas laborables, con los festivos de Sevilla"
            className={`text-xs font-medium ${plazo.apura ? 'text-red-600' : 'text-slate-500'}`}
          >
            {plazo.texto}
          </span>
        )}
        {plazo && puedeAmpliar && (
          <button
            onClick={() => ampliar.mutate(24)}
            disabled={ampliar.isPending}
            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium transition hover:border-marca-500 disabled:opacity-50 dark:border-slate-700"
          >
            +24 h de plazo
          </button>
        )}
      </header>

      {aviso && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {aviso}
        </p>
      )}

      {ficha.motivo_estado && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {ficha.motivo_estado}
        </p>
      )}

      <Bloque titulo="Cliente">
        <Par etiqueta="Nombre" valor={[texto('nombre'), texto('apellido1')].filter(Boolean).join(' ') || null} />
        <Par etiqueta="Teléfono de contacto" valor={ficha.telefono_contacto} />
        <Par etiqueta="Teléfono de origen" valor={ficha.telefono_origen} />
        <Par etiqueta="Correo" valor={texto('email')} />
        <Par etiqueta="Vivienda habitual" valor={texto('vivienda_habitual')} />
        <Par etiqueta="Bono social" valor={texto('bono_social')} />
      </Bloque>

      <Bloque titulo="Suministro">
        <Par etiqueta="CUPS" valor={ficha.cups} mono />
        <Par
          etiqueta="Estado del suministro"
          valor={texto('estado_suministro') && etiquetaDeSuministro(texto('estado_suministro')!)}
        />
        <Par etiqueta="Dirección" valor={direccion || null} ancho />
        <Par etiqueta="Potencia P1" valor={texto('potencia_p1') && `${texto('potencia_p1')} kW`} />
        <Par etiqueta="Potencia P2" valor={texto('potencia_p2') && `${texto('potencia_p2')} kW`} />
        <Par
          etiqueta="Consumo anual"
          valor={texto('consumo_anual') && `${Number(texto('consumo_anual')).toLocaleString('es-ES')} kWh`}
        />
        <Par etiqueta="Autoconsumo" valor={texto('autoconsumo')} />
        <Par etiqueta="Nueva potencia P1" valor={texto('nueva_potencia_p1') && `${texto('nueva_potencia_p1')} kW`} />
      </Bloque>

      <Bloque titulo="Oferta">
        <Par etiqueta="Compañía" valor={compania?.nombre ?? null} />
        <Par etiqueta="Tarifa" valor={tarifa?.nombre_visible ?? ficha.tarifa_codigo} />
        <Par etiqueta="Tipo de gestión" valor={ficha.tipo_gestion} />
        <Par etiqueta="Mantenimiento" valor={texto('sva')} />
        <Par etiqueta="Número de cuenta" valor={ficha.iban ?? (ficha.iban_ultimos && `···· ${ficha.iban_ultimos}`)} mono />
        <Par
          etiqueta="Puntos"
          valor={
            ficha.puntos_computados !== null
              ? `${ficha.puntos_computados} (congelados al activar)`
              : ficha.puntos_estimados !== null
                ? `${ficha.puntos_estimados} estimados`
                : null
          }
        />
      </Bloque>

      <Bloque titulo="Trazabilidad">
        <Par etiqueta="Comercial" valor={ficha.comercial} />
        <Par etiqueta="Abierta" valor={momento(ficha.creado_en)} />
        <Par etiqueta="En este estado desde" valor={momento(ficha.estado_desde)} />
        <Par etiqueta="Activado el" valor={fecha(ficha.activado_el)} />
        <Par etiqueta="De baja el" valor={fecha(ficha.baja_el)} />
        <Par etiqueta="Clase de baja" valor={ficha.baja_tipo} />
        <Par
          etiqueta="Consentimientos"
          valor={ficha.consentimientos.length > 0 ? ficha.consentimientos.join(', ') : null}
          ancho
        />
      </Bloque>

      {texto('notas') && (
        <Bloque titulo="Notas">
          <p className="col-span-full whitespace-pre-wrap text-sm">{texto('notas')}</p>
        </Bloque>
      )}
    </div>
  )
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">{titulo}</h3>
      <dl className="grid gap-3 sm:grid-cols-2">{children}</dl>
    </section>
  )
}

function Par({
  etiqueta,
  valor,
  mono,
  ancho,
}: {
  etiqueta: string
  valor: string | null | undefined | false
  mono?: boolean
  ancho?: boolean
}) {
  // Un campo vacío no dice nada: ocupa sitio y hace el resumen ilegible.
  if (valor === null || valor === undefined || valor === false || valor === '') return null

  return (
    <div className={ancho ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs text-slate-400">{etiqueta}</dt>
      <dd className={`text-sm font-medium ${mono ? 'font-mono' : ''}`}>{valor}</dd>
    </div>
  )
}
